import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import { CmsProject } from '../../types/cms';

const GOLD = '#9d6f32';
const DARK = '#1a1a1a';
const BORDER = '#e8e8e8';

interface ProjectSelectorBarProps {
  projects: CmsProject[];
  currentProject: CmsProject | null;
  onSelectProject: (slug: string) => void;
  onOpenNewModal: () => void;
  onSaveAndPublish: () => void;
  onSaveDraft: () => void;
  onPreviewDraft: () => void;
  hasSavedDraft: boolean;
  onDeleteCurrentProject: () => void;
  saving: boolean;
  hasUnsavedChanges: boolean;
}

export const ProjectSelectorBar: React.FC<ProjectSelectorBarProps> = ({
  projects,
  currentProject,
  onSelectProject,
  onOpenNewModal,
  onSaveAndPublish,
  onSaveDraft,
  onPreviewDraft,
  hasSavedDraft,
  onDeleteCurrentProject,
  saving,
  hasUnsavedChanges,
}) => {
  if (!currentProject) return null;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#fff',
        borderRadius: 2,
        border: `1px solid ${BORDER}`,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
      }}
    >
      {/* Left: Project dropdown & Quick status badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon sx={{ color: GOLD, fontSize: 24 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Active Project:
          </Typography>
        </Box>

        <Select
          size="small"
          value={currentProject.slug}
          onChange={(e) => onSelectProject(e.target.value)}
          sx={{
            minWidth: 220,
            fontWeight: 700,
            fontSize: 14,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
          }}
        >
          {projects.map((p) => (
            <MenuItem key={p.slug} value={p.slug} sx={{ fontWeight: 600 }}>
              🏢 {p.name} ({p.microMarket || p.city})
            </MenuItem>
          ))}
        </Select>

        <Chip
          label={currentProject.isPublished !== false ? '● Published in CMS' : '○ Draft only'}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: 11,
            bgcolor: currentProject.isPublished !== false ? '#e6f4ea' : '#fff3e0',
            color: currentProject.isPublished !== false ? '#137333' : '#b06000',
            border: `1px solid ${currentProject.isPublished !== false ? '#ceead6' : '#ffe0b2'}`,
          }}
        />

        {hasSavedDraft && <Chip label="Saved draft" size="small" color="info" variant="outlined" />}

        {hasUnsavedChanges && (
          <Chip
            label="Unsaved Changes"
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: 11,
              bgcolor: '#fce8e6',
              color: '#c5221f',
              border: '1px solid #fad2cf',
            }}
          />
        )}
      </Box>

      {/* Right: Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onOpenNewModal}
          sx={{
            borderColor: BORDER,
            color: DARK,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: GOLD, bgcolor: 'rgba(157,111,50,0.04)' },
          }}
        >
          New Project
        </Button>

        <Tooltip title="Open the public page to check what visitors can see">
          <Button
            variant="outlined"
            size="small"
            startIcon={<LaunchOutlinedIcon />}
            href={`https://www.swarnagroup.in/projects/${currentProject.slug}`}
            disabled={currentProject.isPublished === false}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderColor: BORDER,
              color: '#4b5563',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
            }}
          >
            Open public page
          </Button>
        </Tooltip>

        <Button variant="outlined" size="small" onClick={onPreviewDraft}>Preview current draft</Button>

        <Button variant="outlined" size="small" onClick={onSaveDraft} disabled={saving || !hasUnsavedChanges}>
          Save draft
        </Button>

        <Button
          variant="contained"
          size="small"
          startIcon={<CloudDoneOutlinedIcon />}
          onClick={onSaveAndPublish}
          disabled={saving}
          sx={{
            bgcolor: GOLD,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            '&:hover': { bgcolor: '#865e2b' },
          }}
        >
          {saving ? 'Saving...' : 'Publish to website database'}
        </Button>

        {projects.length > 1 && (
          <Tooltip title="Delete Project">
            <IconButton
              size="small"
              onClick={onDeleteCurrentProject}
              sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444', bgcolor: '#fee2e2' } }}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
